import type { ServiceResponse } from './types';
import { API_ENDPOINTS } from '../config/api.config';
import { apiRequest } from '../utils/api';

// Backend response types
interface CustomerResponseDto {
  _id: string;
  id: string;
  phone: string;
  name: string;
  email?: string;
  tier: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  totalSpent: number;
  totalOrders: number;
  addresses: any[];
  wishlist: string[];
  isActive: boolean;
  lastOrderDate?: string;
  createdAt: string;
  updatedAt: string;
}

interface OTPResponseDto {
  message: string;
  expiresIn: number; // seconds
}

interface AuthResponseDto {
  customer: CustomerResponseDto | null;
  token: string;
  expiresIn?: number;
  isNewCustomer?: boolean;
  requiresRegistration?: boolean;
  message?: string;
}

// Frontend types
export interface LoginCredentials {
  phone: string;
  otp: string;
}

export interface RegisterData {
  name: string;
  phone: string;
  email?: string;
}

export interface AuthUser {
  id: string;
  name: string;
  phone: string;
  email?: string;
  tier?: 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
  totalSpent?: number;
  totalOrders?: number;
}

export interface AuthResponse {
  user: AuthUser;
  token: string;
}

export interface OTPResponse {
  message: string;
  expiresIn: number;
}

class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  private readonly PHONE_KEY = 'auth_phone';

  // Transform customer to auth user
  private transformCustomer(customer: CustomerResponseDto): AuthUser {
    return {
      id: customer._id || customer.id,
      name: customer.name,
      phone: customer.phone,
      email: customer.email,
      tier: customer.tier,
      totalSpent: customer.totalSpent,
      totalOrders: customer.totalOrders
    };
  }

  // Generate OTP
  async generateOTP(phone: string): Promise<ServiceResponse<OTPResponse>> {
    try {
      const response = await apiRequest<OTPResponseDto>(
        API_ENDPOINTS.AUTH.GENERATE_OTP,
        {
          method: 'POST',
          body: JSON.stringify({ phone }),
        }
      );

      // Store phone for later use
      sessionStorage.setItem(this.PHONE_KEY, phone);

      return {
        success: true,
        data: {
          message: response.message,
          expiresIn: response.expiresIn
        }
      };
    } catch (error) {
      console.error('Error generating OTP:', error);
      return {
        success: false,
        data: null as unknown as OTPResponse,
        error: error instanceof Error ? error.message : 'Failed to generate OTP'
      };
    }
  }

  // Verify OTP and login
  async verifyOTP(phone: string, otp: string, name?: string): Promise<ServiceResponse<AuthResponse & { requiresRegistration?: boolean }>> {
    try {
      const response = await apiRequest<AuthResponseDto>(
        API_ENDPOINTS.AUTH.VERIFY_OTP,
        {
          method: 'POST',
          body: JSON.stringify({ phone, otp, ...(name && { name }) }),
        }
      );

      // Check if registration is required
      if (response.requiresRegistration && !response.customer) {
        // Store phone for registration
        sessionStorage.setItem(this.PHONE_KEY, phone);
        
        return {
          success: true,
          data: {
            user: null as any,
            token: '',
            requiresRegistration: true
          }
        };
      }

      // Normal login flow
      if (!response.customer) {
        throw new Error('No customer data received');
      }

      const authData = {
        user: this.transformCustomer(response.customer),
        token: response.token
      };

      // Store auth data
      localStorage.setItem(this.TOKEN_KEY, response.token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(authData.user));
      sessionStorage.removeItem(this.PHONE_KEY);

      return {
        success: true,
        data: authData
      };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      return {
        success: false,
        data: null as unknown as AuthResponse & { requiresRegistration?: boolean },
        error: error instanceof Error ? error.message : 'Invalid OTP or expired'
      };
    }
  }

  // Login with phone (generates OTP)
  async loginWithPhone(phone: string): Promise<ServiceResponse<OTPResponse>> {
    return this.generateOTP(phone);
  }

  // Complete registration after OTP verification
  async completeRegistration(phone: string, name: string, email?: string): Promise<ServiceResponse<AuthResponse>> {
    try {
      const response = await apiRequest<AuthResponseDto>(
        API_ENDPOINTS.AUTH.COMPLETE_REGISTRATION,
        {
          method: 'POST',
          body: JSON.stringify({ phone, name, email }),
        }
      );

      if (!response.customer) {
        throw new Error('No customer data received');
      }

      const authData = {
        user: this.transformCustomer(response.customer),
        token: response.token
      };

      // Store auth data
      localStorage.setItem(this.TOKEN_KEY, response.token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(authData.user));

      return {
        success: true,
        data: authData
      };
    } catch (error) {
      console.error('Error completing registration:', error);
      return {
        success: false,
        data: null as unknown as AuthResponse,
        error: error instanceof Error ? error.message : 'Registration failed'
      };
    }
  }

  // Register new customer
  async register(data: RegisterData): Promise<ServiceResponse<AuthUser>> {
    try {
      const response = await apiRequest<CustomerResponseDto>(
        API_ENDPOINTS.CUSTOMERS.REGISTER,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );

      const user = this.transformCustomer(response);

      return {
        success: true,
        data: user,
        message: 'Registration successful. Please login with OTP.'
      };
    } catch (error) {
      console.error('Error registering:', error);
      return {
        success: false,
        data: null as unknown as AuthUser,
        error: error instanceof Error ? error.message : 'Registration failed'
      };
    }
  }

  // Get current user from API
  async getCurrentUserFromAPI(): Promise<ServiceResponse<AuthUser>> {
    try {
      const response = await apiRequest<CustomerResponseDto>(
        API_ENDPOINTS.AUTH.ME,
        {},
        true
      );

      const user = this.transformCustomer(response);

      // Update localStorage
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));

      return {
        success: true,
        data: user
      };
    } catch (error) {
      console.error('Error fetching current user:', error);
      return {
        success: false,
        data: null as unknown as AuthUser,
        error: error instanceof Error ? error.message : 'Failed to get user'
      };
    }
  }

  // Logout
  async logout(): Promise<ServiceResponse<void>> {
    try {
      // JWT tokens are stateless, so we just need to clear local storage
      // No need to call a server-side logout endpoint
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
      sessionStorage.removeItem(this.PHONE_KEY);
      
      return {
        success: true,
        data: undefined
      };
    } catch (error) {
      return {
        success: false,
        data: undefined,
        error: 'Logout failed'
      };
    }
  }

  // Get current user
  getCurrentUser(): AuthUser | null {
    try {
      const userStr = localStorage.getItem(this.USER_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  // Get auth token
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  // Get stored phone number (during OTP flow)
  getStoredPhone(): string | null {
    return sessionStorage.getItem(this.PHONE_KEY);
  }

  // Check if authenticated
  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getCurrentUser();
  }

  // Update user profile
  async updateProfile(updates: Partial<RegisterData>): Promise<ServiceResponse<AuthUser>> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        throw new Error('No user logged in');
      }

      const response = await apiRequest<CustomerResponseDto>(
        API_ENDPOINTS.CUSTOMERS.UPDATE(user.id),
        {
          method: 'PUT',
          body: JSON.stringify(updates),
        },
        true
      );

      const updatedUser = this.transformCustomer(response);

      // Update localStorage
      localStorage.setItem(this.USER_KEY, JSON.stringify(updatedUser));

      return {
        success: true,
        data: updatedUser
      };
    } catch (error) {
      console.error('Error updating profile:', error);
      return {
        success: false,
        data: null as unknown as AuthUser,
        error: error instanceof Error ? error.message : 'Failed to update profile'
      };
    }
  }

  // Check if phone exists
  async checkPhoneExists(phone: string): Promise<ServiceResponse<boolean>> {
    try {
      const response = await apiRequest<CustomerResponseDto>(
        API_ENDPOINTS.CUSTOMERS.BY_PHONE(phone),
        {
          method: 'GET',
        }
      );

      return {
        success: true,
        data: !!response
      };
    } catch (error) {
      // If 404, phone doesn't exist
      if (error instanceof Error && error.message.includes('404')) {
        return {
          success: true,
          data: false
        };
      }
      
      return {
        success: false,
        data: false,
        error: 'Failed to check phone'
      };
    }
  }
}

export const authService = new AuthService();